// src/pages/LoginPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import styles from '../styles/LoginPage.module.css';

const LoginPage = () => {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  const from    = location.state?.from?.pathname || '/dashboard';
  const message = location.state?.message;

  // Helper: check employment status in Firestore
  const checkEmploymentActive = async (uid) => {
    try {
      const ref = doc(db, 'users', uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) return { allowed: true }; // no doc → allow
      const data = snap.data();
      const isTerminated = data.employmentStatus === 'Terminated';
      const inactive     = data.active === false;
      if (isTerminated || inactive) {
        const reason = data.terminationReason ? ` Reason: ${data.terminationReason}` : '';
        return {
          allowed: false,
          msg:
            isTerminated
              ? `Your account has been disabled by a manager.${reason}`
              : 'Your account is inactive. Please contact your administrator.',
        };
      }
      return { allowed: true };
    } catch (e) {
      // If we can’t check, don’t lock out by accident—allow, but you can change to block if preferred.
      console.warn('Employment check failed:', e);
      return { allowed: true };
    }
  };

  // If already logged in, validate employment and redirect/block accordingly
  useEffect(() => {
    const guard = async () => {
      if (!currentUser) return;
      const res = await checkEmploymentActive(currentUser.uid);
      if (!res.allowed) {
        await signOut(auth);
        setError(res.msg || 'Your account is not permitted to sign in.');
        return;
      }
      navigate('/dashboard', { replace: true });
    };
    guard();
  }, [currentUser, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
      const uid = auth.currentUser?.uid;
      if (uid) {
        const res = await checkEmploymentActive(uid);
        if (!res.allowed) {
          await signOut(auth);
          setError(res.msg || 'Your account is not permitted to sign in.');
          return; // stay on login page
        }
      }
      navigate(from, { replace: true });
    } catch (err) {
      console.error('Login failed:', err);
      setError('Failed to log in. Check email and password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.pageContainer}>
      <div className={styles.loginBox}>
        <h2>Login</h2>

        {message && <div className={styles.successMessage}>{message}</div>}
        {error && <div className={styles.errorMessage}>{error}</div>}

        <form onSubmit={handleSubmit} className={styles.loginForm}>
          <div className={styles.inputGroup}>
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className={styles.submitButton} disabled={loading}>
            {loading ? <span className={styles.loadingSpinner}></span> : 'Sign In'}
          </button>
        </form>

        {/* Optional extras */}
        {/* <p className={styles.extraLink}>
          <Link to="/forgot-password">Forgot your password?</Link>
        </p> */}

        <p style={{ textAlign: "center", marginTop: 18, fontSize: 13, color: "#64748b" }}>
          <Link to="/about" style={{ color: "#4a90e2", fontWeight: 700, textDecoration: "none" }}>
            تعرّف على المنصة ←
          </Link>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
